import axios from 'axios';
import { DIRECTUS_DATASETS_ENDPOINT, DIRECTUS_FILES_ENDPOINT, fetchCurrentUser, testExternalApi } from '../api';

const buildAuthHeaders = (token) => ({
  Authorization: `Bearer ${token}`
});

const normalizeTargets = (rawTargets) => {
  if (Array.isArray(rawTargets) && rawTargets.length > 0) {
    const normalized = rawTargets
      .map((target) => String(target || '').trim().toLowerCase())
      .filter(Boolean);
    return normalized.length > 0 ? normalized : ['both'];
  }

  if (typeof rawTargets === 'string' && rawTargets.trim().length > 0) {
    return rawTargets
      .split(',')
      .map((target) => target.trim().toLowerCase())
      .filter(Boolean);
  }

  return ['both'];
};

const parseWorkspaceLink = (workspaceLink) => {
  if (workspaceLink == null) return null;
  if (typeof workspaceLink === 'object') {
    const rawId = workspaceLink.id ?? workspaceLink.workspace_id?.id ?? workspaceLink.workspace_id;
    return rawId == null ? null : Number(rawId);
  }
  return Number(workspaceLink);
};

const datasetLinkedToWorkspace = (dataset, workspaceId) => {
  if (!Number.isFinite(Number(workspaceId))) return true;

  const numericWorkspaceId = Number(workspaceId);
  if (Number(dataset.linked_workspace) === numericWorkspaceId) return true;

  const linked = Array.isArray(dataset.linked_workspaces) ? dataset.linked_workspaces : [];
  return linked.some((entry) => Number(parseWorkspaceLink(entry)) === numericWorkspaceId);
};

const mapDatasetToIntegration = (dataset) => {
  const apiConfig = dataset?.api_config || {};
  return {
    id: dataset.id,
    name: dataset.name || `Integration ${dataset.id}`,
    functionName: apiConfig.functionName || null,
    enabled: apiConfig.enabled !== false,
    priority: Number.isFinite(Number(apiConfig.priority)) ? Number(apiConfig.priority) : 50,
    targets: normalizeTargets(dataset.data_sets),
    source: 'workspace'
  };
};

const sortByPriorityDesc = (items) =>
  [...items].sort((a, b) => Number(b.priority || 0) - Number(a.priority || 0));

const shouldFallbackFromApi = (error) => {
  const status = error?.response?.status;
  if (!status) return true; // network/proxy errors
  return [404, 429, 500, 502, 503, 504].includes(status);
};

const DATASET_FIELD_PROFILES = [
  [
    'id',
    'name',
    'type',
    'linked_workspace',
    'linked_workspaces',
    'available_to_all_workspaces',
    'data_sets',
    'api_config'
  ],
  [
    'id',
    'name',
    'type',
    'linked_workspace',
    'available_to_all_workspaces',
    'data_sets',
    'api_config'
  ],
  [
    'id',
    'name',
    'type',
    'linked_workspace',
    'data_sets',
    'api_config'
  ]
];

const fetchDatasetsWithFieldFallback = async (token) => {
  let lastError;
  for (const fields of DATASET_FIELD_PROFILES) {
    try {
      const response = await axios.get(DIRECTUS_DATASETS_ENDPOINT, {
        headers: buildAuthHeaders(token),
        params: {
          fields,
          filter: {
            type: { _eq: 'external_api' }
          },
          limit: -1
        }
      });
      return Array.isArray(response?.data?.data) ? response.data.data : [];
    } catch (error) {
      lastError = error;
      const status = error?.response?.status;
      // Retry only on permission/schema issues; otherwise bubble up immediately.
      if (status && status !== 403 && status !== 400) {
        throw error;
      }
    }
  }
  throw lastError;
};

const fetchWorkspaceIntegrationsFromDirectus = async (token, { target = null, workspaceId = null } = {}) => {
  const datasets = await fetchDatasetsWithFieldFallback(token);
  const integrations = datasets
    .filter((dataset) => datasetLinkedToWorkspace(dataset, workspaceId))
    .map(mapDatasetToIntegration)
    .filter((integration) => {
      if (!target) return true;
      const normalizedTarget = String(target).trim().toLowerCase();
      const targets = normalizeTargets(integration.targets);
      return targets.includes('both') || targets.includes(normalizedTarget);
    });

  const runtimeIntegrations = sortByPriorityDesc(integrations);
  return {
    workspaceIntegrations: integrations,
    runtimeIntegrations,
    defaultIntegrations: [],
    fallbackActive: false
  };
};

export const fetchCurrentUserContext = async () => {
  const payload = await fetchCurrentUser(['id', 'linked_workspace.id']);
  return {
    userId: payload?.id || null,
    workspaceId: payload?.linked_workspace?.id || null
  };
};

export const fetchDatasetsForWorkspace = async (token, workspaceId) => {
  const numericWorkspaceId = Number(workspaceId);
  const hasWorkspaceId = Number.isFinite(numericWorkspaceId);

  const workspaceFilter = hasWorkspaceId
    ? {
        _or: [
          { linked_workspace: { _eq: numericWorkspaceId } },
          { linked_workspaces: { workspace_id: { _eq: numericWorkspaceId } } }
        ]
      }
    : {};

  const response = await axios.get(DIRECTUS_DATASETS_ENDPOINT, {
    headers: buildAuthHeaders(token),
    params: {
      fields: [
        'id',
        'name',
        'type',
        'dataset_type',
        'linked_workspace',
        'linked_workspaces',
        'available_to_all_workspaces',
        'data_sets',
        'date_created',
        'user_created.first_name',
        'user_created.last_name',
        'user_created.email',
        'file.directus_files_id.id',
        'file.directus_files_id.filename_download',
        'file.directus_files_id.title'
      ],
      filter: workspaceFilter,
      sort: ['-date_created'],
      limit: -1
    }
  });
  return response?.data?.data || [];
};

export const createDataset = async (token, datasetData) => {
  const response = await axios.post(DIRECTUS_DATASETS_ENDPOINT, datasetData, {
    headers: {
      ...buildAuthHeaders(token),
      'Content-Type': 'application/json'
    }
  });
  return response?.data?.data;
};

export const uploadDatasetFile = async (token, file, folder = '8e6cd5f5-f7f3-4de4-b507-4804e6f2ad9e') => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('folder', folder);

  const response = await axios.post(DIRECTUS_FILES_ENDPOINT, formData, {
    headers: {
      ...buildAuthHeaders(token),
      'Content-Type': 'multipart/form-data'
    }
  });
  return response?.data?.data;
};

export const testDatasetExternalApi = async (apiConfig) => {
  return testExternalApi(apiConfig);
};

export const fetchWorkspaceIntegrations = async (token, { target = null, workspaceId = null } = {}) => {
  const requestConfig = {
    headers: buildAuthHeaders(token),
    params: target ? { target } : {}
  };

  try {
    const response = await axios.get('/api/integrations', requestConfig);
    return response?.data || null;
  } catch (error) {
    if (!shouldFallbackFromApi(error)) {
      throw error;
    }
    try {
      const fallbackResponse = await axios.get('/workers/api/integrations', requestConfig);
      return fallbackResponse?.data || null;
    } catch (fallbackError) {
      if (!shouldFallbackFromApi(fallbackError)) {
        throw fallbackError;
      }
      return fetchWorkspaceIntegrationsFromDirectus(token, { target, workspaceId });
    }
  }
};

export const updateWorkspaceIntegration = async (token, datasetId, patch) => {
  const requestConfig = {
    headers: {
      ...buildAuthHeaders(token),
      'Content-Type': 'application/json'
    }
  };

  try {
    const response = await axios.patch(`/api/integrations/${datasetId}`, patch, requestConfig);
    return response?.data?.integration || null;
  } catch (error) {
    if (!shouldFallbackFromApi(error)) {
      throw error;
    }

    try {
      const fallbackResponse = await axios.patch(
        `/workers/api/integrations/${datasetId}`,
        patch,
        requestConfig
      );
      return fallbackResponse?.data?.integration || null;
    } catch (fallbackError) {
      if (!shouldFallbackFromApi(fallbackError)) {
        throw fallbackError;
      }

      const directusPatch = {
        api_config: {
          enabled: Boolean(patch?.enabled),
          priority: Number(patch?.priority)
        }
      };

      if (Array.isArray(patch?.targets)) {
        directusPatch.data_sets = patch.targets;
      }

      const directusResponse = await axios.patch(
        `${DIRECTUS_DATASETS_ENDPOINT}/${datasetId}`,
        directusPatch,
        requestConfig
      );
      return mapDatasetToIntegration(directusResponse?.data?.data || { id: datasetId, ...directusPatch });
    }
  }
};

export const fetchWorkspaceIntegrationHealth = async (token, { target = null, workspaceId = null } = {}) => {
  const requestConfig = {
    headers: buildAuthHeaders(token),
    params: target ? { target } : {}
  };

  try {
    const response = await axios.get('/api/integrations/health', requestConfig);
    return Array.isArray(response?.data?.health) ? response.data.health : [];
  } catch (error) {
    if (!shouldFallbackFromApi(error)) {
      throw error;
    }
    try {
      const fallbackResponse = await axios.get('/workers/api/integrations/health', requestConfig);
      return Array.isArray(fallbackResponse?.data?.health) ? fallbackResponse.data.health : [];
    } catch (fallbackError) {
      if (!shouldFallbackFromApi(fallbackError)) {
        throw fallbackError;
      }
      const directusPayload = await fetchWorkspaceIntegrationsFromDirectus(token, { target, workspaceId });
      return (directusPayload.workspaceIntegrations || []).map((integration) => ({
        id: integration.id,
        status: 'unknown',
        message: 'Health endpoint unavailable in current API runtime'
      }));
    }
  }
};
